import { useState } from 'react';
import { Button, Price } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useCommandeBoutique } from '../../api/boutique.js';
import { galerieALaction } from '../../auth/sujet.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { useLangue } from '../../composants/useLangue.js';
import { useShellContext } from '../shell/context.js';
import { ErreurBoutique, EtatCommandeBadge, VignetteBoutique } from './commun.js';
import { etapeSuivante } from './donneesBoutique.js';
import { RembourserDialog } from './RembourserDialog.js';
import styles from './CommandeDetailPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
export function CommandeDetailPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const { id = '' } = useParams();
  const langue = useLangue();
  const requete = useCommandeBoutique(galerieSlug, id);
  const [rembourser, setRembourser] = useState(false);
  const [copie, setCopie] = useState<'ok' | 'echec' | null>(null);
  const retour = (
    <Link className={styles.retour} to={`/${galerieSlug}/boutique`}>
      {t('regie:boutique.retourCommandes')}
    </Link>
  );
  if (requete.isLoading)
    return (
      <div>
        {retour}
        <p role="status">{t('commun:chargement')}</p>
      </div>
    );
  if (requete.isError)
    return (
      <div>
        {retour}
        <ErreurBoutique erreur={requete.error} reessayer={() => void requete.refetch()} />
      </div>
    );
  const c = requete.data;
  if (!c) return null;
  const peutRembourser =
    galerieALaction(galerie, 'orders:refund') &&
    (c.etat === 'payee' || (c.etat === 'a_rembourser' && !!c.facture));
  return (
    <div className={styles.page}>
      <div className={styles.barre}>
        {retour}
        <Verrouillable action="orders:refund" ouvert={peutRembourser}>
          <Button variant="secondary" onClick={() => setRembourser(true)}>
            {t(
              c.etat === 'a_rembourser'
                ? 'regie:boutique.terminerRemboursement'
                : 'regie:boutique.rembourser',
            )}
          </Button>
        </Verrouillable>
      </div>
      <aside
        className={styles.etape}
        data-etat={c.etat}
        aria-label={t('regie:boutique.etapes.titre')}
      >
        <p>{t(`regie:boutique.etatsAide.${c.etat}`)}</p>
        <p className={styles.etapeTexte}>
          <strong>{t('regie:boutique.etapes.titre')}</strong>
          {etapeSuivante(c.etat)
            ? t(`regie:boutique.etapes.${etapeSuivante(c.etat)}`)
            : t('regie:boutique.etapes.aucune')}
        </p>
      </aside>
      <FeuillePiece
        emetteur={galerie.nom ?? galerieSlug}
        nature={t('regie:boutique.detail')}
        destinataire={{
          legende: t('regie:boutique.acheteur'),
          nom: c.contact.nomAffiche,
          complement: (
            <Link className={styles.lien} to={`/${galerieSlug}/contacts/${c.contact.id}`}>
              {t('regie:boutique.voirContact')}
            </Link>
          ),
        }}
        numero={<span className={styles.reference}>{c.reference}</span>}
        tampon={<EtatCommandeBadge etat={c.etat} />}
        etat={c.etat}
        meta={t('regie:boutique.creeeLe', { date: formatDate(new Date(c.creeLe), langue) })}
      >
        <div className={styles.oeuvre}>
          <VignetteBoutique
            galerie={galerieSlug}
            mediaId={c.oeuvre.mediaPrincipalId}
            url={c.oeuvre.apercuUrl}
          />
          <Link className={styles.cartel} to={`/${galerieSlug}/oeuvres/${c.oeuvre.id}`}>
            <ObjetCartel
              titre={c.oeuvre.titre}
              artiste={c.oeuvre.artisteNom}
              taille="lg"
              precisions={[c.exemplaire]}
            />
          </Link>
        </div>
        <div className={styles.total}>
          <span>{t('regie:boutique.montant')}</span>
          <Price cents={c.montantCentimes} lang={langue} size="lg" />
        </div>
        <div className={styles.dates}>
          {c.payeeLe && (
            <p>{t('regie:boutique.payeeLe', { date: formatDate(new Date(c.payeeLe), langue) })}</p>
          )}
          {c.etat === 'en_paiement' && (
            <p>
              {t('regie:boutique.expireLe', {
                date: new Intl.DateTimeFormat(langue, {
                  dateStyle: 'long',
                  timeStyle: 'short',
                  timeZone: 'Europe/Paris',
                }).format(new Date(c.expireLe)),
              })}
            </p>
          )}
        </div>
        {c.motif && <p className={styles.motif}>{t(`regie:boutique.motifs.${c.motif}`)}</p>}
        {c.etat === 'a_rembourser' && <p>{t('regie:boutique.remboursementAttente')}</p>}
        <dl className={styles.pieces}>
          {c.facture && (
            <div>
              <dt>{t('regie:boutique.facture')}</dt>
              <dd>
                <Link className={styles.lien} to={`/${galerieSlug}/ventes/${c.facture.id}`}>
                  {c.facture.numero ?? '—'}
                </Link>
              </dd>
            </div>
          )}
          {c.avoir && (
            <div>
              <dt>{t('regie:boutique.avoir')}</dt>
              <dd>
                <Link className={styles.lien} to={`/${galerieSlug}/ventes/${c.avoir.id}`}>
                  {c.avoir.numero ?? '—'}
                </Link>
              </dd>
            </div>
          )}
          {c.paiementId && (
            <div>
              <dt>{t('regie:boutique.paiement')}</dt>
              <dd className={styles.paiement}>
                <code>{c.paiementId}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!navigator.clipboard) {
                      setCopie('echec');
                      return;
                    }
                    void navigator.clipboard.writeText(c.paiementId!).then(
                      () => setCopie('ok'),
                      () => setCopie('echec'),
                    );
                  }}
                >
                  {t('regie:boutique.copier')}
                </Button>
                {copie && (
                  <span role="status">
                    {t(copie === 'ok' ? 'regie:boutique.copie' : 'regie:boutique.copieEchec')}
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>
      </FeuillePiece>
      {rembourser && peutRembourser && (
        <RembourserDialog galerie={galerieSlug} commande={c} fermer={() => setRembourser(false)} />
      )}
    </div>
  );
}
